// @flow
import type {
  PaymentInfoType,
  PaymentResponseType,
  PaymentAuthentication,
  PaymentAuthenticationResultType,
} from './reducer';

type BringBackTo = {
  bringBackTo: ?'simReplacement' | 'confirmPlan' | 'confirmPlanDuringActivate',
};

export type PaymentActionType =
  | {|
      type: 'SIGNUP_SET_PAYMENT_INFO',
      payload: {|
        paymentInfo: PaymentResponseType,
      |},
    |}
  | {|
      type: 'SIGNUP_SET_PAYMENT_BRAINTREE_CLIENT_TOKEN',
      payload: {|
        braintreeClientToken: string,
      |},
    |}
  | {|
      type: 'SIGNUP_SET_PAYMENT_STRIPE_CLIENT_TOKEN',
      payload: {|
        stripeCustomerId: string,
      |},
    |}
  | {|
      type: 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ORDER_SIM',
      payload: {|
        paymentInfo: PaymentInfoType,
        bringBackTo?: BringBackTo,
        isPaymentMeanUpdate?: boolean,
      |},
    |}
  | {|
      type: 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ACTIVATE',
      payload: {|
        paymentInfo: PaymentInfoType,
        bringBackTo?: BringBackTo,
        isPaymentMeanUpdate?: boolean,
      |},
    |}
  | {|
      type: 'CHANGE_PAYMENT_MEAN_ON_DASHBOARD',
      payload: {|
        paymentInfo: PaymentInfoType,
        bringBackTo?: BringBackTo,
        isPaymentMeanUpdate?: boolean,
      |},
    |}
  | {|
      type: 'SIGNUP_REFRESH_PAYMENT_DURING_ORDER_SIM',
    |}
  | {|
      type: 'SIGNUP_REFRESH_PAYMENT_DURING_ACTIVATE',
    |}
  | {|
      type: 'REFRESH_PAYMENT_DETAILS_ON_DASHBOARD',
    |}
  | {| type: 'FETCH_PAYMENTS' |}
  | {| type: 'FETCH_PAYMENTS_SUCCESS', payments: Object[] |}
  | {| type: 'STRIPE_TIMEOUT' |}
  | {| type: 'CLEAR_STRIPE_ERROR' |}
  | {| type: 'INIT_STRIPE_PAYMENT_MEAN' |}
  | {|
      type: 'CHECK_PAYMENT_AUTHORIZATION_RESULT',
      payload: {| notificationOuid: string, stripeIntentId: string, stripeRedirectUrl: string |},
    |}
  | {| type: 'FETCH_PAYMENT_AUTHENTICATIONS' |}
  | {|
      type: 'SET_PAYMENT_AUTHENTICATIONS',
      payload: {|
        paymentAuthentications: PaymentAuthentication[],
      |},
    |}
  | {|
      type: 'SET_PAYMENT_AUTHENTICATION_RESULT',
      payload: {|
        paymentAuthenticationResult: PaymentAuthenticationResultType,
      |},
    |}
  | {|
      type: 'SET_STRIPE_REDIRECT_URL',
      payload: {|
        stripeRedirectUrl: string,
      |},
    |}
  | {|
      type: 'SET_IS_THREE_D_SECURE_AUTHENT_MODAL_OPEN',
      payload: {|
        isModalOpen: boolean,
      |},
    |};

export const setPaymentInfo = (paymentInfo: PaymentResponseType): PaymentActionType => ({
  type: 'SIGNUP_SET_PAYMENT_INFO',
  payload: {
    paymentInfo,
  },
});

export const setPaymentBraintreeClientToken = (braintreeClientToken: string): PaymentActionType => ({
  type: 'SIGNUP_SET_PAYMENT_BRAINTREE_CLIENT_TOKEN',
  payload: {
    braintreeClientToken,
  },
});

export const setPaymentStripeClientToken = (stripeCustomerId: string): PaymentActionType => ({
  type: 'SIGNUP_SET_PAYMENT_STRIPE_CLIENT_TOKEN',
  payload: {
    stripeCustomerId,
  },
});

export const addPaymentMeanDuringActivate = (options: {
  paymentInfo: PaymentInfoType,
  bringBackTo?: BringBackTo,
  isPaymentMeanUpdate?: boolean,
}): PaymentActionType => ({
  type: 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ACTIVATE',
  payload: {
    paymentInfo: options.paymentInfo,
    bringBackTo: options.bringBackTo,
    isPaymentMeanUpdate: options.isPaymentMeanUpdate,
  },
});

export const addPaymentMeanDuringOrderSim = (options: {
  paymentInfo: PaymentInfoType,
  bringBackTo?: BringBackTo,
  isPaymentMeanUpdate?: boolean,
}): PaymentActionType => ({
  type: 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ORDER_SIM',
  payload: {
    paymentInfo: options.paymentInfo,
    bringBackTo: options.bringBackTo,
    isPaymentMeanUpdate: options.isPaymentMeanUpdate,
  },
});

export const changePaymentMeanOnDashboard = (options: {
  paymentInfo: PaymentInfoType,
  bringBackTo?: BringBackTo,
  isPaymentMeanUpdate?: boolean,
}): PaymentActionType => ({
  type: 'CHANGE_PAYMENT_MEAN_ON_DASHBOARD',
  payload: {
    paymentInfo: options.paymentInfo,
    bringBackTo: options.bringBackTo,
    isPaymentMeanUpdate: options.isPaymentMeanUpdate,
  },
});

export const refreshPaymentDetailsDuringOrderSim = (): PaymentActionType => ({
  type: 'SIGNUP_REFRESH_PAYMENT_DURING_ORDER_SIM',
});

export const refreshPaymentDetailsDuringActivate = (): PaymentActionType => ({
  type: 'SIGNUP_REFRESH_PAYMENT_DURING_ACTIVATE',
});

export const refreshPaymentDetailsOnDashboard = (): PaymentActionType => ({
  type: 'REFRESH_PAYMENT_DETAILS_ON_DASHBOARD',
});

export const fetchPaymentsSuccess = (payments: Object[]): PaymentActionType => ({
  type: 'FETCH_PAYMENTS_SUCCESS',
  payments,
});

export const fetchPayments = (): PaymentActionType => ({
  type: 'FETCH_PAYMENTS',
});

export const initStripePaymentMean = (): PaymentActionType => ({ type: 'INIT_STRIPE_PAYMENT_MEAN' });

export const stripeTimeout = () => ({ type: 'STRIPE_TIMEOUT' });

export const clearStripeTimeout = () => ({ type: 'CLEAR_STRIPE_ERROR' });

export const checkPaymentAuthorizationResult = (
  stripeIntentId: string,
  stripeRedirectUrl: string,
  notificationOuid: string
) => ({
  payload: { notificationOuid, stripeIntentId, stripeRedirectUrl },
  type: 'CHECK_PAYMENT_AUTHORIZATION_RESULT',
});

export const fetchPaymentAuthentications = (): PaymentActionType => ({
  type: 'FETCH_PAYMENT_AUTHENTICATIONS',
});

export const setPaymentAuthentications = (paymentAuthentications: PaymentAuthentication[]): PaymentActionType => ({
  type: 'SET_PAYMENT_AUTHENTICATIONS',
  payload: {
    paymentAuthentications,
  },
});

export const setPaymentAuthenticationResult = (
  paymentAuthenticationResult: PaymentAuthenticationResultType
): PaymentActionType => ({
  type: 'SET_PAYMENT_AUTHENTICATION_RESULT',
  payload: {
    paymentAuthenticationResult,
  },
});

export const setStripeRedirectUrl = (stripeRedirectUrl: string): PaymentActionType => ({
  type: 'SET_STRIPE_REDIRECT_URL',
  payload: { stripeRedirectUrl },
});

export const setIsThreeDSecureAuthentModalOpen = (isModalOpen: boolean): PaymentActionType => ({
  type: 'SET_IS_THREE_D_SECURE_AUTHENT_MODAL_OPEN',
  payload: { isModalOpen },
});
